import { useNavigate } from "react-router-dom";
import { formatTime, useAudioPlayer } from "./player-context.js";

/**
 * 固定在底部的全局播放控制条。
 *
 * 参考主流音乐应用的布局：左侧封面与曲目信息、中间传输控制与进度、右侧音量与辅助操作。
 * 它渲染在应用外壳里而不是某个页面内，因此翻页、滚动、进入后台管理都不会中断播放，
 * 用户始终能看到"正在播放什么、播到哪里了"。
 */
export function PlayerBar() {
  const player = useAudioPlayer();
  const navigate = useNavigate();
  const { current, playing, positionMs, durationMs, loading, error, loop, volume } = player.state;

  if (!current) return null;

  const ratio = durationMs > 0 ? Math.min(positionMs / durationMs, 1) : 0;

  const seekFromEvent = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    player.seek(((event.clientX - rect.left) / rect.width) * durationMs);
  };

  return <footer className="player-bar" style={{ "--song-hue": current.hue } as React.CSSProperties}>
    <div className="pb-progress" onClick={seekFromEvent} role="slider"
      aria-label="播放进度" aria-valuenow={Math.round(ratio * 100)} aria-valuemin={0} aria-valuemax={100}>
      <i style={{ width: `${ratio * 100}%` }} />
      <b style={{ left: `${ratio * 100}%` }} />
    </div>

    <div className="pb-body">
      <div className="pb-track" onClick={() => navigate(`/songs/${current.songNumber}`)} role="button" title="打开歌曲页">
        <span className="pb-cover">{current.unitIcon}</span>
        <span className="pb-meta">
          <b>{current.songTitle}</b>
          <small>{current.unitName} · 版本 {current.variant.variantCode}</small>
        </span>
      </div>

      <div className="pb-controls">
        <button className="pb-btn" onClick={player.previous} title="上一首">⏮</button>
        <button className="pb-btn primary" onClick={() => player.toggle()} title={playing ? "暂停" : "播放"}>
          {loading ? <span className="spinner small" /> : playing ? "❚❚" : "▶"}
        </button>
        <button className="pb-btn" onClick={player.next} title="下一首">⏭</button>
        <span className="pb-time">{formatTime(positionMs)} / {formatTime(durationMs)}</span>
      </div>

      <div className="pb-extra">
        <button className={`pb-btn${loop ? " on" : ""}`} onClick={player.toggleLoop} title="单曲循环">↻</button>
        <input className="pb-volume" type="range" min={0} max={1} step={0.05} value={volume}
          onChange={(event) => player.setVolume(Number(event.target.value))} title="音量" aria-label="音量" />
        <button className="pb-btn" onClick={() => navigate(`/songs/${current.songNumber}`)} title="查看歌词">词</button>
      </div>
    </div>

    {error && <div className="pb-error">{error}</div>}
  </footer>;
}
